/** Plantilla de consentimiento por consultorio (tenant) — PostgreSQL. */
import { fetchRecords, saveRecordApi, RECORD_TYPES } from '../api/consultorioClinical';

export const TEXTO_CONSENTIMIENTO_DEFAULT = `CONSENTIMIENTO INFORMADO PARA ACTUACIONES ODONTOLÓGICAS

Establecimiento: {{consultorio}}

Paciente: {{nombre}}
Identificación: {{tipo_identificacion}} — {{numero_identificacion}}
Fecha de nacimiento: {{fecha_nacimiento}} ({{edad}})
Teléfono de contacto: {{telefono}}
Correo: {{email}}

Yo, {{nombre}}, identificado(a) con {{tipo_identificacion}} N.º {{numero_identificacion}}, declaro que:

1. He recibido información clara, veraz y suficiente sobre el estado de mi salud bucal, el diagnóstico presuntivo o definitivo, el plan de tratamiento propuesto, los procedimientos a realizar, sus beneficios esperados, riesgos conocidos, alternativas razonables y las consecuencias de no aceptar o postergar el tratamiento.

2. Comprendo que la odontología es una ciencia en constante evolución y que pueden presentarse situaciones no previstas que requieran modificar el plan terapéutico, siempre con comunicación al paciente o su representante legal cuando corresponda.

3. Autorizo al equipo de salud bucal de {{consultorio}} a realizar los exámenes clínicos, registros fotográficos o radiográficos necesarios, y los procedimientos acordados, incluyendo el uso de anestesia local, medicación y materiales biomédicos autorizados según normativa vigente en Ecuador.

4. Me han informado sobre el manejo de datos personales y clínicos conforme a la legislación aplicable, y autorizo su archivo en la historia clínica odontológica.

5. Confirmo que pude formular preguntas, que fueron respondidas de manera comprensible, que actúo sin coacción y que los antecedentes declarados en la ficha son veraces.

6. Acepto que este documento, junto con mi firma (electrónica, escaneada o manuscrita digitalizada), constituye constancia del consentimiento informado prestado el {{fecha_larga}}.`;

export const CONSENTIMIENTO_PLACEHOLDERS = [
  { key: '{{nombre}}', label: 'Nombre del paciente', ejemplo: 'María Pérez' },
  { key: '{{tipo_identificacion}}', label: 'Tipo de documento', ejemplo: 'Cédula' },
  { key: '{{numero_identificacion}}', label: 'Número de identificación', ejemplo: '1712345678' },
  { key: '{{cedula}}', label: 'Cédula (alias)', ejemplo: '1712345678' },
  { key: '{{fecha_nacimiento}}', label: 'Fecha de nacimiento', ejemplo: '15/03/1990' },
  { key: '{{edad}}', label: 'Edad', ejemplo: '35 años' },
  { key: '{{telefono}}', label: 'Teléfono', ejemplo: '0991234567' },
  { key: '{{email}}', label: 'Correo electrónico', ejemplo: 'paciente@email.com' },
  { key: '{{consultorio}}', label: 'Nombre del consultorio', ejemplo: 'Clínica Dental Norte' },
  { key: '{{fecha}}', label: 'Fecha (corta)', ejemplo: '21/05/2026' },
  { key: '{{fecha_larga}}', label: 'Fecha (extensa)', ejemplo: '21 de mayo de 2026' },
];

function tenantKey(tenantId, idDoctorLegacy) {
  if (tenantId != null && tenantId !== '') return `t:${String(tenantId)}`;
  if (idDoctorLegacy != null && idDoctorLegacy !== '') return `d:${String(idDoctorLegacy)}`;
  return null;
}

export async function getPlantillaConsentimientoConsultorio(tenantId, idDoctorLegacy = null) {
  const tk = tenantKey(tenantId, idDoctorLegacy);
  if (!tk) {
    return { texto: TEXTO_CONSENTIMIENTO_DEFAULT, nombreConsultorio: '', updatedAt: null };
  }
  try {
    const rows = await fetchRecords(RECORD_TYPES.CONSENT_TEMPLATE);
    const ext = `consent-${tk}`;
    const ent = rows.find((r) => r.id === ext) || {};
    const texto = String(ent.texto || '').trim() || TEXTO_CONSENTIMIENTO_DEFAULT;
    return {
      texto,
      nombreConsultorio: String(ent.nombreConsultorio || '').trim(),
      updatedAt: ent.updatedAt || null,
    };
  } catch {
    return { texto: TEXTO_CONSENTIMIENTO_DEFAULT, nombreConsultorio: '', updatedAt: null };
  }
}

export async function savePlantillaConsentimientoConsultorio(
  tenantId,
  texto,
  nombreConsultorio = '',
  idDoctorLegacy = null,
) {
  const tk = tenantKey(tenantId, idDoctorLegacy);
  if (!tk) return false;
  try {
    await saveRecordApi(RECORD_TYPES.CONSENT_TEMPLATE, `consent-${tk}`, {
      texto: String(texto || '').trim() || TEXTO_CONSENTIMIENTO_DEFAULT,
      nombreConsultorio: String(nombreConsultorio || '').trim(),
      updatedAt: new Date().toISOString(),
    });
    return true;
  } catch {
    return false;
  }
}

function calcularEdad(fechaNac) {
  if (!fechaNac) return '';
  const hoy = new Date();
  const nac = new Date(fechaNac);
  let edad = hoy.getFullYear() - nac.getFullYear();
  if (hoy.getMonth() < nac.getMonth() || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())) {
    edad--;
  }
  return `${edad} años`;
}

export function buildDatosPacienteConsent(form, nombreConsultorio = '') {
  const nombre = (form?.nombre || '').trim() || '________________';
  const tipo = (form?.tipoIdentificacion || '').trim() || 'Documento';
  const num = String(form?.cedula || '').trim() || '________________';
  const fechaNac = form?.fechaNacimiento
    ? new Date(form.fechaNacimiento).toLocaleDateString('es-EC')
    : '________________';
  const hoy = new Date();
  const consultorio =
    (nombreConsultorio || '').trim() ||
    (form?.nombreConsultorio || '').trim() ||
    'este establecimiento de salud bucal';

  return {
    '{{nombre}}': nombre,
    '{{tipo_identificacion}}': tipo,
    '{{numero_identificacion}}': num,
    '{{cedula}}': num,
    '{{fecha_nacimiento}}': fechaNac,
    '{{edad}}': form?.fechaNacimiento ? calcularEdad(form.fechaNacimiento) : '________________',
    '{{telefono}}': (form?.telefono || '').trim() || '________________',
    '{{email}}': (form?.email || '').trim() || '________________',
    '{{consultorio}}': consultorio,
    '{{fecha}}': hoy.toLocaleDateString('es-EC'),
    '{{fecha_larga}}': hoy.toLocaleDateString('es-EC', { dateStyle: 'long' }),
  };
}

export function aplicarPlaceholdersPlantilla(texto, datos) {
  let out = String(texto || '');
  Object.entries(datos || {}).forEach(([key, val]) => {
    out = out.split(key).join(val ?? '');
  });
  return out;
}

export async function renderDocumentoConsentimiento(form, tenantId, idDoctorLegacy = null) {
  const plantilla = await getPlantillaConsentimientoConsultorio(tenantId, idDoctorLegacy);
  const personal = String(form?.consentimientoInformadoTexto || '').trim();
  const base = personal || plantilla.texto;
  const datos = buildDatosPacienteConsent(form, plantilla.nombreConsultorio);
  return aplicarPlaceholdersPlantilla(base, datos);
}

export async function resolveTextoConsentimientoPaciente(form, idDoctorOrTenant, idDoctorLegacy = null) {
  const tenantId = typeof idDoctorOrTenant === 'number' || String(idDoctorOrTenant || '').startsWith('t:')
    ? idDoctorOrTenant
    : null;
  const legacy = tenantId ? idDoctorLegacy : idDoctorOrTenant;
  return renderDocumentoConsentimiento(form, tenantId || null, legacy || idDoctorOrTenant);
}

export function firmaConsentimientoValida(dataUrl) {
  const s = String(dataUrl || '');
  return s.startsWith('data:image') || s.startsWith('data:application/pdf') || /\.(pdf|png|jpe?g|webp)(\?|$)/i.test(s);
}

export function labelModoFirmaConsentimiento(m) {
  if (m === 'dibujo') return 'Firma dibujada en pantalla (firma electrónica simple)';
  if (m === 'archivo_pdf') return 'Documento PDF firmado o escaneado';
  if (m === 'archivo') return 'Imagen de firma (foto o escaneo)';
  return 'Sin firma registrada';
}

export function etiquetaIdentificacionPacienteConsent(form) {
  const tipo = (form.tipoIdentificacion || '').trim();
  const num = String(form.cedula || '').trim();
  if (!num) return { ok: false, texto: '' };
  const t = tipo || 'Documento';
  return { ok: true, texto: `${t}: ${num}` };
}

export function pacienteUsaTextoPersonalizadoConsent(form) {
  return String(form?.consentimientoInformadoTexto || '').trim().length > 0;
}
